import React, { useState, useEffect, useRef } from "react";
import CytoscapeComponent from "react-cytoscapejs";
import {
  initialNodeStyle,
  initialDirectedWeightedEdgeStyle,
} from "./GraphStyle";
import "./CytoscapeGraph.css";
import Dijkstra from "./Dijkstra";
import DAGS from "./DAGS";
import BellmanFord from "./BellmanFord";

const DWG = ({ algorithmType }) => {
  const cyStyle = {
    width: "100%",
    height: "100%",
  };

  const resetState = () => {
    setNodes([]);
    setEdges([]);
    setVisited([]);
    setQueue([]);
    setAdjacencyList({});
    setGenerateGraph(false);
    setEdgeWeights({});
  };

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [visited, setVisited] = useState([]);
  const [queue, setQueue] = useState([]);
  const [cytoscapeReady, setCytoscapeReady] = useState(false);

  const [numNodes, setNumNodes] = useState(2); // Number of nodes
  const [adjacencyList, setAdjacencyList] = useState({}); // Adjacency list
  const [generateGraph, setGenerateGraph] = useState(false);
  const [edgeWeights, setEdgeWeights] = useState({});
  const [selectedStartingNode, setSelectedStartingNode] = useState(1);

  // Create a ref to store the Cytoscape instance
  const cytoscapeRef = useRef(null);

  useEffect(() => {
    if (generateGraph) {
      const generatedElements = generateGraphElements(numNodes, adjacencyList);
      setNodes(generatedElements.nodes);
      setEdges(generatedElements.edges);
      if (cytoscapeRef.current) {
        cytoscapeRef.current.fit();
      }
    }
  }, [generateGraph, numNodes, adjacencyList]);

  const generateGraphElements = (numNodes, adjacencyList) => {
    const generatedNodes = [];
    const generatedEdges = [];

    // Calculate the number of rows and columns for the grid
    const numRows = Math.ceil(Math.sqrt(numNodes));
    const numCols = Math.ceil(numNodes / numRows);

    // Calculate the spacing between nodes
    const spacingX = 300 / numCols;
    const spacingY = 300 / numRows;

    // Generate nodes in a grid
    for (let row = 0; row < numRows; row++) {
      for (let col = 0; col < numCols; col++) {
        const nodeId = row * numCols + col + 1;
        if (nodeId <= numNodes) {
          const positionY = row * spacingY + (col % 2 === 0 ? 0 : spacingY / 2);
          generatedNodes.push({
            data: { id: `node${nodeId}`, label: `${nodeId}` },
            position: { x: col * spacingX, y: positionY },
          });
        }
      }
    }

    for (const sourceNode in adjacencyList) {
      const targetNodes = adjacencyList[sourceNode];
      targetNodes.forEach((targetNode) => {
        const edgeId = `${sourceNode}-${targetNode}`;
        const weight = edgeWeights[edgeId] || "";
        generatedEdges.push({
          data: {
            id: edgeId,
            source: `node${sourceNode}`,
            target: `node${targetNode}`,
            weight: weight,
          },
        });
      });
    }
    return { nodes: generatedNodes, edges: generatedEdges };
  };

  const handleNumNodesChange = (e) => {
    const newNumNodes = parseInt(e.target.value);
    if (newNumNodes >= 2 && newNumNodes <= 10) {
      setNumNodes(newNumNodes);
      if (selectedStartingNode > newNumNodes) {
        setSelectedStartingNode(newNumNodes);
      }
    } else if (newNumNodes < 2) {
      setNumNodes(2); // Minimum value of 2
    } else if (newNumNodes > 10) {
      setNumNodes(10); // Maximum value of 10
    }
  };

  const handleAdjacencyChange = (node, adjacentNode) => {
    const updatedAdjacencyList = { ...adjacencyList };

    if (!updatedAdjacencyList[node]) {
      updatedAdjacencyList[node] = [];
    }

    const nodeIndex = updatedAdjacencyList[node].indexOf(adjacentNode);

    if (nodeIndex !== -1) {
      // If the edge (node -> adjacentNode) exists, remove it
      updatedAdjacencyList[node].splice(nodeIndex, 1);
    } else {
      // If the edge (node -> adjacentNode) doesn't exist, add it
      updatedAdjacencyList[node].push(adjacentNode);
    }

    setAdjacencyList(updatedAdjacencyList);
  };

  const allEdgesWeighted = () => {
    for (const sourceNode in adjacencyList) {
      const targetNodes = adjacencyList[sourceNode];
      for (const targetNode of targetNodes) {
        const edgeId = `${sourceNode}-${targetNode}`;
        const reversedEdgeId = `${targetNode}-${sourceNode}`;

        // Check if the weight has been entered for either direction
        if (!edgeWeights[edgeId] && !edgeWeights[reversedEdgeId]) {
          return false;
        }
      }
    }
    return true;
  };

  const handleEdgeWeightChange = (edgeId, weight) => {
    //const numericWeight = parseInt(weight);
    if (algorithmType == "dijkstra" && (isNaN(weight) || weight < 0)) {
      //console.log("Invalid weight:", weight);
      alert("Please enter a non-negative numeric weight.");
      return;
    }
    setEdgeWeights((prevEdgeWeights) => {
      // Update the edge weight for the provided edge
      const newEdgeWeights = { ...prevEdgeWeights };
      newEdgeWeights[edgeId] = weight;

      // Split the edgeId into source and target nodes
      const [sourceNode, targetNode] = edgeId.split("-");

      // Create a copy of the adjacency list
      const updatedAdjacencyList = { ...adjacencyList };

      // Update the weights for one direction
      newEdgeWeights[`${sourceNode}-${targetNode}`] = weight;

      setAdjacencyList(updatedAdjacencyList);
      return newEdgeWeights;
    });
  };

  const handleGenerateGraph = () => {
    setNodes([]);
    setEdges([]);
    if (!allEdgesWeighted()) {
      alert("Please enter weights for all edges before generating the graph.");
      return;
    }
    if (selectedStartingNode > numNodes) {
      setSelectedStartingNode(numNodes);
    }
    setGenerateGraph((prevGenerateGraph) => {
      if (!prevGenerateGraph) {
        setNodes([]);
        setEdges([]);

        const generatedElements = generateGraphElements(
          numNodes,
          adjacencyList
        );
        setNodes(generatedElements.nodes);
        setEdges(generatedElements.edges);
      }
      return !prevGenerateGraph;
    });
  };

  const handleCytoscapeReady = (cy) => {
    if (cy) {
      cy.style()
        .fromJson([
          {
            selector: "node",
            style: {
              ...initialNodeStyle,
            },
          },
          {
            selector: "edge",
            style: {
              ...initialDirectedWeightedEdgeStyle,
            },
          },
        ])
        .update();

      setCytoscapeReady(true);
    }
  };

  return (
    <div className="graph-container">
      <div className="left-column">
        <div className="graph-options">
          {algorithmType === "dijkstra" && (
            <label className="graph-option">
              <b>
                Go ahead and create a directed weighted graph. Dijkstra's
                algorithm doesn't support negative weighted edges.
              </b>
            </label>
          )}
          {algorithmType === "dags" && (
            <label className="graph-option">
              <b>
                Go ahead and create a directed acyclic weighted graph. DAG
                shortest path algorithm doesn't support negative weight cycles.
                Here topological sort happens in descending order of numbers.
              </b>
            </label>
          )}
          {algorithmType === "bellmanFord" && (
            <label className="graph-option">
              <b>
                Go ahead and create a directed weighted graph. In Bellman Ford
                if there are any negative weight cycles, the loop continues
                |G.V|-1 times and keeps updating the distances.
              </b>
            </label>
          )}
        </div>
        <div className="node-input">
          <label className="node-label">Number of Nodes:</label>
          <input
            className="node-input-field"
            type="number"
            value={numNodes}
            onChange={handleNumNodesChange}
            min="2"
            max="10"
          />
        </div>
        <div className="node-input">
          {algorithmType === "dijkstra" && (
            <label className="node-label">Select Starting Node:</label>
          )}
          {algorithmType === "dijkstra" && (
            <input
              className="node-input-field"
              type="number"
              value={selectedStartingNode}
              onChange={(e) =>
                setSelectedStartingNode(parseInt(e.target.value))
              }
              min="1"
              max={numNodes}
            />
          )}
          {algorithmType === "dags" && (
            <label className="node-label">
              Starting Node: First node of the topological sort order.
            </label>
          )}
          {algorithmType === "bellmanFord" && (
            <label className="node-label">
              Starting Node: First node of the graph which is 1. <br />
              Edges are selected in descending order.
            </label>
          )}
        </div>
        <div className="adjacency-list">
          <label className="adjacency-label">Adjacency List:</label>
          <ul className="adjacency-list-ul">
            {Array.from({ length: numNodes }, (_, i) => i + 1).map((node) => (
              <li key={node}>
                <b>Node {node}:</b>&nbsp;&nbsp;&nbsp;
                {Array.from({ length: numNodes }, (_, i) => i + 1)
                  .filter((adjNode) => adjNode !== node)
                  .map((adjNode) => (
                    <div key={adjNode} className="adjacency-list-entry">
                      <input
                        type="checkbox"
                        checked={(adjacencyList[node] || []).includes(adjNode)}
                        onChange={() => handleAdjacencyChange(node, adjNode)}
                      />
                      {adjNode}
                      &nbsp;&nbsp;&nbsp;
                      <input
                        type="number"
                        placeholder="Weight"
                        value={edgeWeights[`${node}-${adjNode}`] || ""}
                        onChange={(e) =>
                          handleEdgeWeightChange(
                            `${node}-${adjNode}`,
                            e.target.value
                          )
                        }
                        style={{ width: "69px" }}
                        disabled={!adjacencyList[node]?.includes(adjNode)}
                      />
                    </div>
                  ))}
              </li>
            ))}
          </ul>
        </div>
        <div className="graph-controls">
          <button
            className="generate-reset-button"
            onClick={handleGenerateGraph}
          >
            Generate Graph
          </button>
          &nbsp;&nbsp;
          <button className="generate-reset-button" onClick={resetState}>
            Reset Graph
          </button>
        </div>
      </div>
      <div className="right-column">
        <div className="cytoscape-container ">
          <CytoscapeComponent
            elements={nodes.concat(edges)}
            style={cyStyle}
            cy={(cy) => {
              cy.userZoomingEnabled(false);
              cytoscapeRef.current = cy;
              handleCytoscapeReady(cy);
            }}
          />
        </div>
        {algorithmType === "dijkstra" && (
          <Dijkstra
            cytoscape={cytoscapeRef.current}
            nodes={nodes}
            visited={visited}
            queue={queue}
            setVisited={setVisited}
            setQueue={setQueue}
            startingNode={selectedStartingNode}
          />
        )}
        {algorithmType === "dags" && (
          <DAGS
            cytoscape={cytoscapeRef.current}
            nodes={nodes}
            visited={visited}
            queue={queue}
            setVisited={setVisited}
            setQueue={setQueue}
            startingNode={selectedStartingNode}
          />
        )}
        {algorithmType === "bellmanFord" && (
          <BellmanFord
            cytoscape={cytoscapeRef.current}
            nodes={nodes}
            visited={visited}
            queue={queue}
            setVisited={setVisited}
            setQueue={setQueue}
            startingNode={selectedStartingNode}
          />
        )}
      </div>
    </div>
  );
};

export default DWG;
